import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCountries } from "@/hooks/useCountries";
import {
  DocumentMagnifyingGlassIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { formatDistanceToNow } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { RiskBadge } from "./RiskBadge";
import { useDocumentReviews } from "../hooks/useDocumentReview";
import { formatFileSize } from "@/shared/core";
import type { RecentReviewsWidgetProps } from "../types/component.types";

export const RecentReviewsWidget: React.FC<RecentReviewsWidgetProps> = ({
  basePath,
  limit = 3,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getCountryName } = useCountries();
  const locale = i18n.language === "es" ? es : enUS;

  const { reviews, loading } = useDocumentReviews();

  const handleViewAll = () => {
    navigate(`${basePath}/document-review`);
  };

  const handleViewReview = (id: string) => {
    navigate(`${basePath}/document-review/${id}`);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircleIcon className="h-4 w-4 text-atlantis-600" />;
      case "failed":
        return <XCircleIcon className="h-4 w-4 text-red-600" />;
      case "processing":
        return (
          <div className="h-4 w-4 border-2 border-tiber-600 border-t-transparent rounded-full animate-spin" />
        );
      default:
        return <ClockIcon className="h-4 w-4 text-titan" />;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-tiber-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <DocumentMagnifyingGlassIcon className="h-6 w-6 text-tiber-600" />
          <h3 className="text-lg font-semibold text-juridico">
            {t("documentReview.title")}
          </h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="h-8 w-8 border-4 border-tiber-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-tiber-600 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <DocumentMagnifyingGlassIcon className="h-6 w-6 text-tiber-600" />
          <h3 className="text-lg font-semibold text-juridico">
            {t("documentReview.title")}
          </h3>
        </div>
        <button
          onClick={handleViewAll}
          className="text-sm text-tiber-600 hover:text-tiber-700 font-medium flex items-center gap-1"
        >
          {t("common.viewAll")}
          <ArrowRightIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Content */}
      {reviews.length === 0 ? (
        <div className="text-center py-8">
          <DocumentMagnifyingGlassIcon className="h-12 w-12 text-tiber-300 mx-auto mb-3" />
          <p className="text-sm text-titan mb-4">
            {t("documentReview.noReviews")}
          </p>
          <button
            onClick={handleViewAll}
            className="text-sm text-tiber-600 hover:text-tiber-700 font-medium"
          >
            {t("documentReview.uploadDocument")}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.slice(0, limit).map((review) => (
            <div
              key={review.id}
              onClick={() => handleViewReview(review.id)}
              className="p-3 rounded-lg border border-tiber-200 hover:bg-romance-50 cursor-pointer transition-colors"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-juridico truncate">
                    {review.document_name}
                  </h4>
                  <div className="flex items-center gap-2 text-xs text-titan mt-1">
                    <span>{review.document_type}</span>
                    <span>•</span>
                    <span>{getCountryName(review.country)}</span>
                    {review.file_size && (
                      <>
                        <span>•</span>
                        <span>{formatFileSize(review.file_size)}</span>
                      </>
                    )}
                  </div>
                </div>
                {review.analysis_status === "completed" &&
                  review.analysis_result?.overall_risk && (
                    <RiskBadge
                      risk={review.analysis_result.overall_risk}
                      size="sm"
                    />
                  )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getStatusIcon(review.analysis_status)}
                  <span className="text-xs text-tiber-700">
                    {t(`documentReview.status.${review.analysis_status}`)}
                  </span>
                </div>
                <span className="text-xs text-titan">
                  {formatDistanceToNow(new Date(review.created_at), {
                    addSuffix: true,
                    locale,
                  })}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
